import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Case, CaseStatus, Counselor } from '../../types';
import { t } from '../../utils/translations';
import { needsCaseReassignment, translateIssueType } from './casesUtils';
import {
  filterAssignableCounselors,
  formatCounselorOptionLabel,
  rankCounselorsForCase,
} from './assignmentUtils';

export type ReassignAssignmentFields = Pick<
  Case,
  | 'assignedCounselorId'
  | 'assignedCounselorName'
  | 'proposedCounselorId'
  | 'proposedCounselorName'
  | 'assignmentStatus'
  | 'status'
>;

interface ReassignCaseDialogProps {
  open: boolean;
  caseItem: Case | null;
  counselors: Counselor[];
  inactiveUserIds?: ReadonlySet<string> | string[];
  onClose: () => void;
  onSubmit: (assignment: ReassignAssignmentFields) => void | Promise<void>;
}

const ReassignCaseDialog: React.FC<ReassignCaseDialogProps> = ({
  open,
  caseItem,
  counselors,
  inactiveUserIds,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [counselorId, setCounselorId] = useState('');
  const [forceAssign, setForceAssign] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const rankedCounselors = useMemo(() => {
    if (!caseItem) return [];
    const inactive =
      inactiveUserIds instanceof Set
        ? inactiveUserIds
        : new Set(inactiveUserIds ?? []);
    const keep = new Set(
      [caseItem.proposedCounselorId, caseItem.assignedCounselorId].filter(
        (id): id is string => Boolean(id)
      )
    );
    const assignable = filterAssignableCounselors(counselors, inactive, keep);
    return rankCounselorsForCase(caseItem.issueTypes, assignable, caseItem.sex);
  }, [caseItem, counselors, inactiveUserIds]);

  useEffect(() => {
    if (!open || !caseItem) return;
    const current =
      caseItem.proposedCounselorId || caseItem.assignedCounselorId || '';
    setCounselorId(needsCaseReassignment(caseItem) ? '' : current);
    setForceAssign(false);
    setError('');
    setSubmitting(false);
  }, [open, caseItem]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!caseItem || submitting) return;
    if (!counselorId) {
      setError(t.cases.reassignCounselorRequired);
      return;
    }

    const selected = counselors.find((c) => c.id === counselorId);
    if (!selected) {
      setError(t.cases.reassignCounselorRequired);
      return;
    }

    const assignment: ReassignAssignmentFields = forceAssign
      ? {
          assignedCounselorId: counselorId,
          assignedCounselorName: selected.fullName,
          proposedCounselorId: null,
          proposedCounselorName: null,
          assignmentStatus: 'forced',
          status:
            caseItem.status === 'waiting' ? 'active' : (caseItem.status as CaseStatus),
        }
      : {
          assignedCounselorId: undefined,
          assignedCounselorName: undefined,
          proposedCounselorId: counselorId,
          proposedCounselorName: selected.fullName,
          assignmentStatus: 'pending',
          status: 'waiting',
        };

    try {
      setSubmitting(true);
      await onSubmit(assignment);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
      }}
      disableEscapeKeyDown={submitting}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          border: fullScreen ? 'none' : '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor:
            caseItem && needsCaseReassignment(caseItem) ? '#FEF2F2' : 'background.paper',
        }}
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
          {t.cases.reassignTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t.cases.reassignSubtitle}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
          {caseItem && (
            <Box
              sx={{
                mb: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                {caseItem.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {caseItem.counseledName}
              </Typography>
              {caseItem.issueTypes.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                  {caseItem.issueTypes.map((issueType) => (
                    <Chip key={issueType} size="small" label={translateIssueType(issueType)} />
                  ))}
                </Box>
              )}
              {needsCaseReassignment(caseItem) && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1.25 }}>
                  {t.cases.reassignUnassignedHint}
                </Typography>
              )}
            </Box>
          )}

          <FormControl fullWidth error={Boolean(error)}>
            <InputLabel id="reassign-counselor-label">
              {t.cases.assignedCounselor} *
            </InputLabel>
            <Select
              labelId="reassign-counselor-label"
              value={counselorId}
              label={`${t.cases.assignedCounselor} *`}
              onChange={(event) => {
                setCounselorId(String(event.target.value));
                if (error) setError('');
              }}
            >
              <MenuItem value="">
                <em>Nealocat</em>
              </MenuItem>
              {rankedCounselors.map((counselor, index) => (
                <MenuItem key={counselor.id} value={counselor.id}>
                  {formatCounselorOptionLabel(counselor, { recommended: index === 0 })}
                </MenuItem>
              ))}
            </Select>
            {error && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {error}
              </Typography>
            )}
          </FormControl>

          {counselorId && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
              <Checkbox
                id="reassign-force-assign"
                checked={forceAssign}
                onChange={(e) => setForceAssign(e.target.checked)}
                size="small"
                sx={{ pt: 0.25 }}
              />
              <Box>
                <Typography
                  component="label"
                  htmlFor="reassign-force-assign"
                  variant="body2"
                  sx={{ cursor: 'pointer', display: 'inline-block' }}
                >
                  {t.assignments.forceAssign}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t.assignments.forceAssignHint}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button onClick={handleClose} disabled={submitting} variant="outlined" color="inherit">
            {t.common.cancel}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={submitting || !counselorId}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {forceAssign ? t.cases.reassignForce : t.cases.reassignPropose}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ReassignCaseDialog;
